<template>
  <v-container fluid class="studies-page pa-0">
    <v-progress-linear
      v-if="loading"
      indeterminate
      class="mb-4"
    />

    <!-- Bloc superior -->
    <section class="hero-section">
      <v-row class="align-stretch" no-gutters>
        <v-col cols="12" md="6" class="hero-left">
          <v-card class="hero-image-card" elevation="4">
            <v-img
              src='/images/img_estudios.jpg'
              class="hero-image"
              cover
            />
          </v-card>
        </v-col>

        <v-col cols="12" md="6" class="hero-right">
          <div class="intro-box">
            <p>
              Bienvenido al espacio donde tus campañas dejan de ser “acciones en marcha”
              para convertirse en información útil, visual y accionable. Aquí podrás
              consultar todos los estudios realizados con
              <strong>
                Geobox, nuestra herramienta de geomarketing para saber dónde impactar,
                a quién llegar y qué zonas tienen más potencial.
              </strong>
              Encontrarás tanto campañas activas como un histórico de estudios, porque
              mirar atrás también ayuda a afinar el siguiente impacto.
            </p>

            <p>
              Cada estudio reúne datos clave como áreas de influencia, secciones censales,
              isocronas desde el punto de venta, perfiles demográficos, información
              económica, hábitos de consumo y otros indicadores adaptados a cada campaña.
              Dicho de forma sencilla:
              <strong>
                menos intuición, más mapa; menos “vamos a probar”, más decisiones con criterio.
              </strong>
            </p>

            <p>
              Este apartado está pensado
              <strong>
                para que puedas consultar, comparar y valorar la evolución de tus campañas
              </strong>
              de forma clara, ordenada y fácil de entender. Porque cuando los datos están
              bien puestos sobre la mesa, las campañas llegan mucho más lejos.
            </p>
          </div>
        </v-col>
      </v-row>
    </section>

    <!-- Secció inferior -->
    <section class="studies-section">
      <v-row class="align-start" no-gutters>
        <!-- Último estudio -->
        <v-col cols="12" md="3" class="latest-column">
          <h2 class="section-title">ÚLTIMO ESTUDIO</h2>

          <v-hover
            v-if="firstItem"
            v-slot="{ isHovering, props: hoverProps }"
          >
            <v-card
              v-bind="hoverProps"
              class="study-card"
              elevation="0"
              :style="{
                transform: isHovering ? 'scale(1.015)' : 'scale(1)',
                transition: 'transform .2s ease'
              }"
              @click="openUrl(firstItem.url)"
            >
              <v-img
                :src="firstItem.image || placeholder(firstItem.id)"
                height="210"
                cover
                class="study-image"
              />

              <div class="study-content">
                <v-card-title
                  class="study-title"
                  :title="firstItem.name"
                >
                  {{ firstItem.name }}
                </v-card-title>

                <v-card-text class="study-text">
                  <div class="line-clamp">
                    {{ firstItem.description || '—' }}
                  </div>

                  <div class="study-link-wrapper">
                    <a
                      :href="firstItem.url"
                      class="study-link"
                      target="_blank"
                      rel="noopener"
                      @click.stop
                    >
                      Abrir documento
                      <v-icon size="15" class="ml-1">mdi-open-in-new</v-icon>
                    </a>
                  </div>
                </v-card-text>
              </div>
            </v-card>
          </v-hover>

          <v-card v-else class="study-card empty-card" elevation="0">
            <v-img
              src="/images/img_estudios.jpg"
              height="210"
              cover
            />
            <v-card-text>
              No hay estudios disponibles.
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Histórico -->
        <v-col cols="12" md="9" class="history-column">
          <h2 class="section-title">HISTÓRICO DE ESTUDIOS</h2>

          <div class="history-wrapper">
            <v-btn
              v-if="canScrollLeft"
              icon
              variant="outlined"
              color="primary"
              class="slider-btn slider-btn-left"
              @click="scrollHistory('left')"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>

            <div ref="historyScroller" class="history-scroller">
              <v-hover
                v-for="it in otherItems"
                :key="it.id"
                v-slot="{ isHovering, props: hoverProps }"
              >
                <v-card
                  v-bind="hoverProps"
                  class="study-card history-card"
                  elevation="0"
                  :style="{
                    transform: isHovering ? 'scale(1.015)' : 'scale(1)',
                    transition: 'transform .2s ease'
                  }"
                  @click="openUrl(it.url)"
                >
                  <v-img
                    :src="it.image || placeholder(it.id)"
                    height="210"
                    cover
                    class="study-image"
                  />

                  <div class="study-content">
                    <v-card-title
                      class="study-title"
                      :title="it.name"
                    >
                      {{ it.name }}
                    </v-card-title>

                    <v-card-text class="study-text">
                      <div class="line-clamp">
                        {{ it.description || '—' }}
                      </div>

                      <div class="study-link-wrapper">
                        <a
                          :href="it.url"
                          class="study-link"
                          target="_blank"
                          rel="noopener"
                          @click.stop
                        >
                          Abrir documento
                          <v-icon size="15" class="ml-1">mdi-open-in-new</v-icon>
                        </a>
                      </div>
                    </v-card-text>
                  </div>
                </v-card>
              </v-hover>
            </div>

            <v-btn
              v-if="canScrollRight"
              icon
              variant="outlined"
              color="primary"
              class="slider-btn slider-btn-right"
              @click="scrollHistory('right')"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>

          <v-alert
            v-if="!loading && firstItem && !otherItems.length"
            type="info"
            variant="tonal"
            class="mt-4"
          >
            No hay más estudios en el histórico.
          </v-alert>
        </v-col>
      </v-row>
    </section>

    <v-alert
      v-if="error"
      type="error"
      class="ma-4"
    >
      {{ error }}
    </v-alert>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, onBeforeUnmount } from 'vue'
import axios from 'axios'

const props = defineProps({
  clientId: {
    type: [Number, String],
    default: null,
  },
  type: {
    type: [Number, String],
    default: null,
  },
})

const API_PREFIX = '/api/admin'

const items = ref([])
const loading = ref(false)
const error = ref('')
const search = ref('')

const ClientId = ref('')
const clientSession = ref({})
const admin = ref(false)
const ClientLogo = ref('')
const ClientImage = ref('')

const historyScroller = ref(null)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)

const placeholder = (id) => `https://picsum.photos/seed/documento-${id}/600/300`

const openUrl = (url) => {
  if (!url) return
  window.open(url, '_blank', 'noopener')
}

const token = localStorage.getItem('token')

if (token) {
  axios.defaults.headers.common.Authorization = `Bearer ${token}`
}

const fetchDocs = async () => {
  loading.value = true
  error.value = ''

  try {
    const params = {
      idClient: ClientId.value,
      type: 1,
    }

    const { data } = await axios.get(`${API_PREFIX}/documentacios`, {
      headers: { Accept: 'application/json' },
      params,
    })

    items.value = Array.isArray(data) ? data : []

    await nextTick()
    updateScrollButtons()
  } catch (e) {
    console.error(e)
    error.value =
      e.response?.data?.message ||
      e.message ||
      'Error cargando la documentación'
  } finally {
    loading.value = false
  }
}

const filteredItems = computed(() => {
  const term = (search.value || '').toLowerCase()

  return items.value.filter((it) => {
    const matchText =
      (it.name || '').toLowerCase().includes(term) ||
      (it.description || '').toLowerCase().includes(term) ||
      (it.url || '').toLowerCase().includes(term)

    const matchClient = props.clientId
      ? String(it.idClient) === String(props.clientId)
      : true

    const matchType =
      props.type !== null &&
      props.type !== undefined &&
      props.type !== ''
        ? String(it.type) === String(props.type)
        : true

    return matchText && matchClient && matchType
  })
})

const firstItem = computed(() => filteredItems.value[0] || null)
const otherItems = computed(() => filteredItems.value.slice(1))

const updateScrollButtons = () => {
  const el = historyScroller.value

  if (!el) {
    canScrollLeft.value = false
    canScrollRight.value = false
    return
  }

  canScrollLeft.value = el.scrollLeft > 5
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 5
}

const scrollHistory = (direction) => {
  const el = historyScroller.value
  if (!el) return

  const amount = 360

  el.scrollBy({
    left: direction === 'left' ? -amount : amount,
    behavior: 'smooth',
  })

  setTimeout(updateScrollButtons, 350)
}

onMounted(async () => {
  const data = localStorage.getItem('clientSession')

  if (data) {
    clientSession.value = JSON.parse(data)

    admin.value = clientSession.value.role === 'admin'
    ClientId.value = clientSession.value.client_id
    ClientLogo.value = clientSession.value.logo
    ClientImage.value = clientSession.value.image
  }

  await fetchDocs()

  window.addEventListener('resize', updateScrollButtons)

  if (historyScroller.value) {
    historyScroller.value.addEventListener('scroll', updateScrollButtons)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateScrollButtons)

  if (historyScroller.value) {
    historyScroller.value.removeEventListener('scroll', updateScrollButtons)
  }
})
</script>

<style scoped>
.studies-page {
  min-height: 100vh;
  background: rgb(var(--v-theme-background));
  color: rgb(var(--v-theme-on-background));
  overflow-x: hidden;
}

/* Bloc superior */
.hero-section {
  padding: 28px 48px 18px;
}

.hero-left {
  padding-right: 22px;
}

.hero-right {
  padding-left: 22px;
  display: flex;
  align-items: center;
}

.hero-image-card {
  width: 100%;
  height: 285px;
  border: 1px solid rgb(var(--v-theme-primary));
  border-radius: 8px;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 3px 8px rgba(var(--v-theme-on-background), 0.25);
}

.hero-image {
  width: 100%;
  height: 100%;
}

.intro-box {
  width: 100%;
  padding: 28px 32px;
  background: rgba(var(--v-theme-surface), 0.92);
  color: rgb(var(--v-theme-on-surface));
  font-size: 15px;
  line-height: 1.48;
  border-radius: 4px;
}

.intro-box p {
  margin: 0 0 20px;
}

.intro-box p:last-child {
  margin-bottom: 0;
}

/* Secció inferior */
.studies-section {
  padding: 16px 48px 36px;
}

.latest-column {
  padding-right: 34px;
}

.history-column {
  padding-left: 8px;
}

.section-title {
  margin: 0 0 20px;
  font-size: 24px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0.5px;
  color: rgb(var(--v-theme-on-background));
}

/* Cards */
.study-card {
  width: 100%;
  max-width: 315px;
  min-width: 315px;
  border: 3px solid rgb(var(--v-theme-primary));
  border-radius: 8px;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  cursor: pointer;
}

.latest-column .study-card {
  max-width: 315px;
  min-width: 0;
}

.study-image {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.16);
}

.study-content {
  min-height: 112px;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
}

.study-title {
  display: block;
  padding: 12px 14px 4px !important;
  font-size: 15px;
  font-weight: 500;
  color: rgb(var(--v-theme-on-surface));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.study-text {
  padding: 4px 14px 12px !important;
  font-size: 13px;
  color: rgb(var(--v-theme-on-surface));
}

.line-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  min-height: 39px;
  overflow: hidden;
}

.study-link-wrapper {
  margin-top: 8px;
}

.study-link {
  display: inline-flex;
  align-items: center;
  color: rgb(var(--v-theme-primary));
  font-size: 12px;
  font-weight: 700;
  text-decoration: underline;
}

.empty-card {
  cursor: default;
}

/* Slider històric */
.history-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.history-scroller {
  display: flex;
  gap: 18px;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  padding: 0 10px 8px 0;
}

.history-scroller::-webkit-scrollbar {
  height: 0;
}

.history-scroller {
  scrollbar-width: none;
}

.history-card {
  flex: 0 0 315px;
}

.slider-btn {
  position: absolute;
  z-index: 5;
  width: 36px;
  height: 36px;
  border-color: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-primary)) !important;
  background: rgb(var(--v-theme-surface)) !important;
}

.slider-btn-left {
  left: -44px;
}

.slider-btn-right {
  right: -44px;
}

/* Responsive */
@media (max-width: 1279px) {
  .hero-section,
  .studies-section {
    padding-left: 28px;
    padding-right: 28px;
  }

  .section-title {
    font-size: 30px;
  }

  .study-card,
  .history-card {
    max-width: 290px;
    min-width: 290px;
    flex-basis: 290px;
  }
}

@media (max-width: 959px) {
  .hero-section,
  .studies-section {
    padding: 20px;
  }

  .hero-left,
  .hero-right,
  .latest-column,
  .history-column {
    padding-left: 0;
    padding-right: 0;
  }

  .hero-right {
    margin-top: 22px;
  }

  .intro-box {
    padding: 22px 18px;
  }

  .studies-section {
    padding-top: 8px;
  }

  .latest-column {
    margin-bottom: 34px;
  }

  .section-title {
    font-size: 28px;
  }

  .study-card {
    max-width: 100%;
    min-width: 100%;
  }

  .history-card {
    flex: 0 0 290px;
    min-width: 290px;
    max-width: 290px;
  }

  .slider-btn-left {
    left: 4px;
  }

  .slider-btn-right {
    right: 4px;
  }
}

@media (max-width: 599px) {
  .hero-image-card {
    height: 220px;
  }

  .section-title {
    font-size: 24px;
  }

  .history-card {
    flex: 0 0 260px;
    min-width: 260px;
    max-width: 260px;
  }

  .study-card {
    min-width: 260px;
  }
}
</style>